import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

const apiUrl = process.env.REACT_APP_API_URL
const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')
axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

// ** Fetch Users
export const fetchUsers = createAsyncThunk('appUsers/fetchData', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get(apiUrl + '/system/users', params)
    return response.data.data
  } catch (error) {
    // return error.message
     // Check if error is rejected with value
     if (error.response && error.response.status === 422) {
      // Extract error message and errors from response data
      const { message, errors } = error.response.data

      // Construct error object with message and errors
      const errorObj = { message, errors }

      // Reject the promise with the error object
      return rejectWithValue(errorObj)
    } else {
      // If there's an error other than 422, reject the promise with the error message
      if(error.response.status===403){
        return rejectWithValue(error.response.status)
      }
      return rejectWithValue(error.message || 'An error occurred')
    }
  }
})

// ** Add User
export const addUser = createAsyncThunk('appUsers/addUser', async (data, { getState, dispatch }) => {
  try {
    const response = await axios.post(apiUrl + '/system/users', data)
    dispatch(fetchUsers(getState().appUsers?.params))
    return response.data
  } catch (error) {
    // Check if error is rejected with value
    if (error.response && error.response.status === 422) {
      // Extract error message and errors from response data
      const { message, errors } = error.response.data

      // Construct error object with message and errors
      const errorObj = { message, errors }

      // Reject the promise with the error object
      return rejectWithValue(errorObj)
    } else {
      // If there's an error other than 422, reject the promise with the error message
      return rejectWithValue(error.message || 'An error occurred')
    }
  }
})

// ** Edit User
export const editUser = createAsyncThunk('appUsers/editUser', async (data, { getState, dispatch, rejectWithValue }) => {
  try {
    const response = await axios.put(apiUrl + `/system/users/${data.id}`, data)
    dispatch(fetchUsers(getState().appUsers?.params))
    return response.data.data
  } catch (error) {
    // Check if error is rejected with value
    if (error.response && error.response.status === 422) {
      // Extract error message and errors from response data
      const { message, errors } = error.response.data

      // Construct error object with message and errors
      const errorObj = { message, errors }

      // Reject the promise with the error object
      return rejectWithValue(errorObj)
    } else {
      // If there's an error other than 422, reject the promise with the error message
      return rejectWithValue(error.message || 'An error occurred')
    }
  }
})

// ** Delete User
export const deleteUser = createAsyncThunk('appUsers/deleteUser', async (id, { getState, dispatch }) => {
  try {
    const response = await axios.delete(apiUrl + `/system/users/${id}`)
    dispatch(fetchUsers(getState().appUsers?.params))
    return id;
  } catch (error) {
    // Check if error is rejected with value
    if (error.response && error.response.status === 500) {
      // Extract error message and errors from response data
      const { message, errors } = error.response.data

      // Construct error object with message and errors
      const errorObj = { message, errors }

      // Reject the promise with the error object
      return rejectWithValue(errorObj)
    } else {
      // If there's an error other than 422, reject the promise with the error message
      return rejectWithValue(error.message || 'An error occurred')
    }
  }
})



const saveToRedux = (state, payload) => {
  if (state.data === payload.data) return

  if (payload.data) {
    state.data = payload.data
    state.total = payload.total
    state.nextPageUrl = payload.next_page_url
    state.prevPageUrl = payload.prev_page_url
  } else {
    state.message = payload
  }
}

export const appUsersSlice = createSlice({
  name: 'appUsers',
  initialState: {
    data: [], // Array to store districts
    message: '', // Message from API response
    nextPageUrl: null,
    prevPageUrl: null,
    total: 0,
    loading: false
  },
  reducers: {},
  extraReducers: builder => {
    // API fetching state
    builder.addCase(fetchUsers.pending, state => {
      state.loading = true // Set loading true to show loading screen
    })

    // API response received state (SUCCESS)
    builder.addCase(fetchUsers.fulfilled, (state, action) => {
      // Assuming action.payload is the response from the API
      saveToRedux(state, action.payload) // Saving districts to redux
      state.loading = false // Set loading false to hide loading screen
    })

    // API response received state (FAILURE)
    builder.addCase(fetchUsers.rejected, (state, action) => {
      state.message = action.payload?.message || 'Failed to fetch user!'
      state.loading = false // Set loading false to hide loading screen
    })

    builder.addCase(deleteUser.pending, state => {
      state.loading = true // Set loading true to show loading screen
    })

    // API response received state (SUCCESS)
    builder.addCase(deleteUser.fulfilled, (state, action) => {
      // Assuming action.payload is the response from the API
      state.loading = false // Set loading false to hide loading screen
      state.roles = state.appUsers?.filter(user => user.id !== action.payload)
    })

    // API response received state (FAILURE)
    builder.addCase(deleteUser.rejected, (state, action) => {
      state.message = action.payload?.message || 'Failed to delete user!'
      state.loading = false // Set loading false to hide loading screen
    })

    builder.addCase(addUser.pending, state => {
      state.loading = true // Set loading true to show loading screen
    })

    // API response received state (SUCCESS)
    builder.addCase(addUser.fulfilled, (state, action) => {
      // Assuming action.payload is the response from the API
      saveToRedux(state, action.payload) // Saving users to redux
      state.loading = false // Set loading false to hide loading screen
    })

    // API response received state (FAILURE)
    builder.addCase(addUser.rejected, (state, action) => {
      state.message = action.payload?.message || 'Failed to add user!'
      state.loading = false // Set loading false to hide loading screen
    })

    builder.addCase(editUser.pending, state => {
      state.loading = true // Set loading true to show loading screen
    })

    // API response received state (SUCCESS)
    builder.addCase(editUser.fulfilled, (state, action) => {
      // Assuming action.payload is the response from the API
      saveToRedux(state, action.payload) // Saving users to redux
      state.loading = false // Set loading false to hide loading screen
    })

    // API response received state (FAILURE)
    builder.addCase(editUser.rejected, (state, action) => {
      state.message = action.payload?.message || 'Failed to edit user!'
      state.loading = false // Set loading false to hide loading screen
    })
  }
})

export default appUsersSlice.reducer
